import type {ReactNode} from "react";
import {ChevronRight, MoreHorizontal, Trash2} from "lucide-react";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export type AdminRowActionItem = {
  label: string;
  onSelect: () => void;
  destructive?: boolean;
  separatorBefore?: boolean;
};

type Props = {
  mode?: "menu" | "open";
  onOpen?: () => void;
  items?: AdminRowActionItem[];
  className?: string;
  openLabel?: string;
};

export function AdminRowActions({mode = "open", onOpen, items, className, openLabel = "Open"}: Props) {
  if(mode === "open" || !items?.length) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn("admin-row-actions h-8 w-8 rounded-full border border-border/70 bg-background", className)}
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.();
        }}
        aria-label={openLabel}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("admin-row-actions h-8 w-8 rounded-full border border-border/70 bg-background", className)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
        {items.map((item) => (
          <div key={item.label}>
            {item.separatorBefore ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              className={item.destructive ? "text-destructive focus:text-destructive" : undefined}
              onSelect={() => item.onSelect()}
            >
              {item.label}
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AdminRowActionsCell({children, className}: {children?: ReactNode; className?: string}) {
  return (
    <div className={cn("flex items-center justify-end gap-1.5", className)} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  );
}

/** Trash icon for list rows — parent should wrap with confirm dialog */
export function AdminRowDeleteButton({
  onDelete,
  label = "Delete",
  className
}: {
  onDelete: () => void;
  label?: string;
  className?: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        "admin-row-actions admin-row-actions--danger h-8 w-8 rounded-full border border-border/70 bg-background text-muted-foreground hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive",
        className
      )}
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      aria-label={label}
      title={label}
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );
}

export function AdminRowChevron() {
  return (
    <span className="admin-modern-table__chevron" aria-hidden>
      <ChevronRight className="h-4 w-4" />
    </span>
  );
}

/**
 * Trailing list controls: optional extras (e.g. toggle) → delete → chevron.
 * Use with AdminModernTable getRowActions.
 */
export function AdminRowTrailing({
  onDelete,
  deleteLabel = "Delete",
  children
}: {
  onDelete?: () => void;
  deleteLabel?: string;
  children?: ReactNode;
}) {
  return (
    <AdminRowActionsCell>
      {children}
      {onDelete ? <AdminRowDeleteButton onDelete={onDelete} label={deleteLabel} /> : null}
      <AdminRowChevron />
    </AdminRowActionsCell>
  );
}
